 
import * as Sentry from '@sentry/browser'
import ErrorBoundary from 'components/ErrorBoundary/ErrorBoundary'
import 'core-js/stable'
import { initializeApp, getApps } from 'firebase/app'
import { browserSessionPersistence, getAuth, setPersistence } from 'firebase/auth'
import { jwtDecode } from 'jwt-decode'
import { IToken } from 'models/interfaces'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { getLocalStorageToken } from 'store/localStorage'
import { getClient } from './client'
import AppContainer from './containers/AppContainer'
import 'styles/index.css'
import 'react-loading-skeleton/dist/skeleton.css'
import * as serviceWorker from './serviceWorker'
import { getStore } from './store'
import './utils/i18/i18n'
import { activePreviews, applyPreviewParam } from 'utils/previewFlags'
import { Provider } from 'react-redux'
import { createTheme, ThemeProvider } from '@mui/material'
 

// Brand palette aligned with Tailwind custom colors (src/index.css @theme).
// MUI 5 changed default TextField variant from "standard" to "outlined" — restore MUI 4 defaults.
const theme = createTheme({
  palette: {
    primary: {
      main: '#463e8f',    // primary-800 — white text contrast 6.73:1 (AAA)
      light: '#615a99',   // primary-700
      dark: '#2b2087',    // primary-900
    },
    secondary: {
      main: '#f16ca9',    // secondary-800
      light: '#ec85b5',   // secondary-700
      dark: '#f9509e',    // secondary-900
    },
  },
  components: {
    MuiTextField: {
      defaultProps: {
        variant: 'standard',
      },
    },
    MuiSelect: {
      defaultProps: {
        variant: 'standard',
      },
    },
  },
})

const __BACKEND_ADDRESS__ = import.meta.env.VITE_BACKEND_ADDRESS as string | undefined
const __PROD__ = import.meta.env.PROD
const __TEST__ = import.meta.env.MODE === 'test'
const __SENTRY_DSN__ = import.meta.env.VITE_SENTRY_DSN as string | undefined

// Guard on DSN presence so a missing CI secret degrades to "no Sentry"
// instead of failing the build. This makes the EU transition safe to roll
// out one step at a time (code first, secret second).
if (__PROD__ && __SENTRY_DSN__) {
  Sentry.init({
    dsn: __SENTRY_DSN__,
    // Includes the commit SHA because package.json version is frozen at 1.10.7
    // — without it every build ever shipped reports the same release and you
    // cannot tell from an event whether a fix is deployed. See vite.config.ts.
    release: `${import.meta.env.VITE_NAME}@${import.meta.env.VITE_VERSION}+${__COMMIT_SHA__}`,
    environment: window.location.hostname,
    ignoreErrors: [
      // Chrome extension noise
      /performanceMetrics/,
      // Browser extensions and non-app errors
      /ResizeObserver loop/,
      /Non-Error promise rejection captured/,
      // Post-deploy chunk-load class — already handled by RouteErrorBoundary's
      // "Flexime har uppdaterats" fallback (T6.9). User reloads, problem gone.
      // No actionable Sentry signal.
      /Failed to fetch dynamically imported module/,
      /Importing a module script failed/,
      /Loading chunk \d+ failed/,
      /Could not load "util"/,
      /'text\/html' is not a valid JavaScript MIME type/,
      // 404 returning HTML for missing JS chunk — same class as above
      /Unexpected token '?</,
      // User-environment network failures (wifi died, captive portal, etc.).
      // Not actionable; happen constantly on mobile/flaky connections.
      /^TypeError: Failed to fetch$/,
      /^Error: Failed to fetch$/,
      /^TypeError: Failed to fetch \(/,
      /^Error: Network Error$/,
      /^TypeError: Load failed$/,
      /^TypeError: Load failed \(/,
      // (User typed the wrong password — suppressed at errorLink layer
      // via SUPPRESSED_ERROR_MESSAGES in client.ts; FRONTEND-5 2026-06-17
      // showed this never matched because T3.7 changed the wrap prefix
      // from "Error:" to "GraphQLError:".)
      // FLEXIME-AX/B1 root cause was fixed by T3.3 (browserSessionPersistence,
      // commit 15946c5b). Suppress any residual reports as a defensive belt-and-
      // braces in case any other code path still touches IndexedDB.
      /INTERNAL ASSERTION FAILED: Pending promise was never set/,
      /Connection to Indexed Database server lost/,
      // User-facing backend errors that ALSO reach Sentry via Apollo's own
      // rejection path (culprit @apollo/client/core/QueryManager), bypassing
      // our errorLink. SUPPRESSED_ERROR_CODES / SUPPRESSED_ERROR_MESSAGES in
      // client.ts only gate OUR capture — this SDK-level filter catches every
      // path. FRONTEND-7 was 58 events / 23 users of pure noise (2026-09-07);
      // ExportTimestamps already shows the user a toast for it.
      /No timestamps found/,
      // Same class: the UI surfaces these on the FileImport screens.
      /No cells to update for fileImport/,
    ],
    beforeBreadcrumb(breadcrumb) {
      // Drop noisy UI breadcrumbs (mouse moves, etc.)
      if (breadcrumb.category === 'ui.input') return null
      return breadcrumb
    },
  })

  const token = getLocalStorageToken()
  if (token) {
    const username = jwtDecode<IToken>(token)?.user?.username ?? null
    if (username) {
      Sentry.setUser({ username })
    }
  }
}
if (!getApps().find((app) => app.name === '[DEFAULT]')) {
  initializeApp(
    {
      measurementId: 'G-4WL89WV28X',
      apiKey: 'AIzaSyDrw5fIKRB2Pel-eg0D67bBCcok6LN6wQg',
      authDomain: 'flexime-frontend-sso.firebaseapp.com',
      projectId: 'flexime-frontend-sso',
      storageBucket: 'flexime-frontend-sso.firebasestorage.app',
      messagingSenderId: '240554934876',
      appId: '1:240554934876:web:8c827a9f67b8c9fa689c99',
    },
    { automaticDataCollectionEnabled: false },
  )
}
// Use session persistence to avoid IndexedDB (FLEXIME-AX / FLEXIME-B1 root cause).
// Firebase Auth state isn't load-bearing for our app — we exchange the id_token
// for our own JWT in the SSO flow and keep that in localStorage. Firebase's own
// persistence only matters during the popup exchange, so dropping IndexedDB has
// no UX impact. The unhandledrejection recovery handler below stays as a backstop.
setPersistence(getAuth(), browserSessionPersistence).catch((err) => {
  if (__PROD__) {
    Sentry.captureMessage('Firebase setPersistence failed', {
      level: 'warning',
      extra: { error: String(err) },
    })
  }
})
const backendUrls = {
  development: 'http://localhost:8080/graphql',
  test: 'https://dev.flexime.io/graphql',
  production: __BACKEND_ADDRESS__ ? __BACKEND_ADDRESS__ : 'http://46.21.109.69:3000/graphql',
}
const store = getStore()
export let BACKEND_URL: string | undefined = undefined
try {
  if (__PROD__ && __BACKEND_ADDRESS__) {
    BACKEND_URL = backendUrls['production']
  } else if (__PROD__) {
    BACKEND_URL = `https://${window.location.hostname}/graphql`
  } else if (__TEST__) {
    BACKEND_URL = backendUrls['test']
  } else if (!__PROD__ && !__TEST__) {
    BACKEND_URL = backendUrls['development']
  }
} catch (err) {
  console.error('Error trying to get backend URL: [', err, '] using localhost url')
  BACKEND_URL = backendUrls['development']
}
// Recover from corrupted Firebase Auth IndexedDB state (FLEXIME-AX / FLEXIME-B1).
// When Firefox/Edge IndexedDB is full or partially corrupted, Firebase Auth's persistence
// throws "INTERNAL ASSERTION FAILED: Pending promise was never set" and the app silently
// fails to fetch data. Clear the corrupted DB and reload once per tab session.
const FIREBASE_RECOVERED_KEY = 'firebase-recovered-this-session'
window.addEventListener('unhandledrejection', async (event) => {
  const msg = String(event.reason?.message ?? event.reason ?? '')
  const isFirebaseAssertion =
    msg.includes('INTERNAL ASSERTION FAILED') || msg.includes('Pending promise was never set')
  if (isFirebaseAssertion && !sessionStorage.getItem(FIREBASE_RECOVERED_KEY)) {
    sessionStorage.setItem(FIREBASE_RECOVERED_KEY, '1')
    Sentry.captureMessage('Firebase IndexedDB recovery triggered', {
      level: 'warning',
      tags: { recovery: 'firebase-idb' },
      extra: { errorMessage: msg },
    })
    await Sentry.flush(1500).catch(() => {
      // best-effort send before reload
    })
    try {
      indexedDB.deleteDatabase('firebaseLocalStorageDb')
    } catch {
      // best-effort
    }
    window.location.reload()
  }
})
// Mark sessions that just recovered, so subsequent Sentry events can be correlated.
if (sessionStorage.getItem(FIREBASE_RECOVERED_KEY)) {
  Sentry.setTag('recovered-from-firebase-idb', '1')
}

// ?preview=<name> / ?preview=off — per-browser switches for features that are
// deployed but not yet on for everyone (see utils/previewFlags.ts). Applied
// before the router mounts so the parameter is gone from the URL it sees.
applyPreviewParam()
// Tag this browser's Sentry events, so an error seen while checking a preview
// can be told apart from one real users hit.
const previews = activePreviews()
if (previews.length > 0) {
  Sentry.setTag('preview', previews.join(','))
}

const root = createRoot(document.getElementById('root')!)
root.render(
  <ThemeProvider theme={theme}>
    <Provider store={store}>
      <ErrorBoundary>
        <AppContainer store={store} client={getClient(BACKEND_URL)} />
      </ErrorBoundary>
    </Provider>
  </ThemeProvider>,
)
// registerServiceWorker();
serviceWorker.unregister()
